
import React, { useState } from 'react';
import { ShieldCheck, Sparkles, Camera, Loader2, AlertTriangle, CheckCircle2, DollarSign, ArrowRight, Zap, Info, FileText } from 'lucide-react';
import { GoogleGenAI, Type } from "@google/genai";
import { Order, Client } from './types';
import { Card, Badge } from './SharedComponents';

export const QualityAnalysisPage = ({ orders, clients, onApplyFee }: any) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState<{damageType: string, severity: string, cost: string, advice: string} | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const analyzeDamage = async (imageSrc: string) => {
    setAnalyzing(true);
    setSelectedImage(imageSrc);
    try {
      // Initialize Gemini API client correctly using process.env.API_KEY
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      
      // Convert base64 format for Gemini (remove data:image/jpeg;base64, if present)
      const base64Data = imageSrc.split(',')[1] || imageSrc;

      const prompt = "Analise esta imagem de um material de evento avariado. Identifique detalhadamente: 1. Tipo de dano. 2. Severidade. 3. Sugestão de valor de reparo. 4. Conselho técnico.";

      // Call generateContent with multimodal input and responseSchema for structured JSON output
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview', // Using gemini-3-flash-preview for multimodal analysis
        contents: {
          parts: [
            { text: prompt },
            { inlineData: { mimeType: 'image/jpeg', data: base64Data } }
          ]
        },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              damageType: {
                type: Type.STRING,
                description: 'O tipo específico de dano identificado (ex: rasgo, quebra, mancha).',
              },
              severity: {
                type: Type.STRING,
                description: 'A gravidade da avaria: Baixa, Média ou Alta.',
              },
              cost: {
                type: Type.STRING,
                description: 'Valor estimado de reparo ou multa em R$.',
              },
              advice: {
                type: Type.STRING,
                description: 'Recomendação técnica para a equipe de manutenção ou controladoria.',
              },
            },
            required: ["damageType", "severity", "cost", "advice"],
          }
        }
      });

      // Use the .text property to access the generated JSON string
      const jsonStr = response.text?.trim() || "";
      
      if (jsonStr) {
        try {
          const parsedResult = JSON.parse(jsonStr);
          setAiResult(parsedResult);
        } catch (parseError) {
          console.error("Failed to parse Gemini response as JSON:", parseError);
          // Fallback parsing if needed
          setAiResult({
            damageType: "Dano Identificado por IA",
            severity: "Média",
            cost: "R$ 150,00",
            advice: jsonStr.slice(0, 150) + "..."
          });
        }
      }
    } catch (error) {
      console.error("Gemini API multimodal analysis error:", error);
      setAiResult({
        damageType: "Falha na análise",
        severity: "N/A",
        cost: "N/A",
        advice: "Não foi possível processar a imagem. Revise manualmente."
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const demoImages = [
    "https://images.unsplash.com/photo-1596200238196-6e42650085d7?auto=format&fit=crop&w=400&q=80",
    "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=400&q=80",
    "https://images.unsplash.com/photo-1620012253295-c05718565a62?auto=format&fit=crop&w=400&q=80"
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-3">
            <ShieldCheck size={32} className="text-blue-600" /> Auditoria de Qualidade & IA
          </h1>
          <p className="text-gray-500 text-sm font-medium">Prevenção de prejuízos e análise automatizada de avarias.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Painel de Upload/Teste */}
        <Card className="p-8 space-y-8 border-2 border-dashed border-blue-100">
           <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner">
                 <Sparkles size={40} className="animate-pulse" />
              </div>
              <div>
                <h3 className="text-xl font-black text-gray-900 uppercase">Análise Visual Inteligente</h3>
                <p className="text-sm text-gray-500">Selecione uma foto de avaria reportada pela equipe externa.</p>
              </div>
           </div>

           <div className="grid grid-cols-3 gap-4">
              {demoImages.map((img, idx) => (
                <button 
                  key={idx} 
                  onClick={() => analyzeDamage(img)}
                  disabled={analyzing}
                  className="relative group rounded-2xl overflow-hidden border-4 border-white shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  <img src={img} className="w-full h-32 object-cover" alt="Demo Avaria" />
                  <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/20 transition-all flex items-center justify-center">
                     <Zap size={24} className="text-white opacity-0 group-hover:opacity-100" />
                  </div>
                </button>
              ))}
           </div>

           <div className="pt-4 text-center">
              <button className="px-8 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase text-xs tracking-widest flex items-center gap-3 mx-auto hover:bg-black transition-all">
                 <Camera size={20} /> Carregar Foto do Local
              </button>
           </div>
        </Card>

        {/* Resultado da Auditoria IA */}
        <div className="space-y-6">
           {analyzing ? (
             <div className="bg-white p-20 rounded-[40px] shadow-xl border border-gray-100 flex flex-col items-center justify-center text-center gap-6">
                <Loader2 size={64} className="text-blue-600 animate-spin" />
                <div>
                   <h4 className="text-2xl font-black uppercase tracking-tight text-gray-900">IA Analisando Pixels...</h4>
                   <p className="text-gray-400 font-medium italic mt-2">Identificando padrões de danos estruturais e teciduais.</p>
                </div>
             </div>
           ) : aiResult ? (
             <div className="bg-white p-8 rounded-[40px] shadow-2xl border-4 border-blue-50 animate-in zoom-in-95 duration-300">
                <div className="flex items-center gap-4 mb-8">
                   <div className="bg-green-500 text-white p-3 rounded-2xl">
                      <ShieldCheck size={32} />
                   </div>
                   <div>
                      <h4 className="text-2xl font-black uppercase tracking-tight text-gray-900">Relatório Gerado por IA</h4>
                      <p className="text-xs text-green-600 font-bold uppercase tracking-widest">Auditoria Concluída com Sucesso</p>
                   </div>
                </div>

                <div className="space-y-6">
                   <div className="grid grid-cols-2 gap-4">
                      <ResultBox label="Tipo de Dano" value={aiResult.damageType} icon={AlertTriangle} color="text-amber-600" />
                      <ResultBox label="Severidade" value={aiResult.severity} icon={ShieldCheck} color="text-red-600" />
                      <ResultBox label="Custo Sugerido" value={aiResult.cost} icon={DollarSign} color="text-blue-600" />
                      <ResultBox label="Status Operacional" value="Bloqueado para Uso" icon={Info} color="text-gray-400" />
                   </div>

                   <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Parecer Técnico IA:</p>
                      <p className="text-sm font-medium text-gray-700 leading-relaxed italic">"{aiResult.advice}"</p>
                   </div>

                   <div className="pt-4 flex gap-4">
                      <button 
                        onClick={() => onApplyFee('DEMO', aiResult.damageType)}
                        className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 flex items-center justify-center gap-2"
                      >
                         <CheckCircle2 size={18} /> Aplicar Taxa ao Cliente
                      </button>
                      <button className="px-6 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-gray-200 transition-all">
                         <FileText size={18} />
                      </button>
                   </div>
                </div>
             </div>
           ) : (
             <div className="bg-gray-100 p-20 rounded-[40px] border-4 border-dashed border-gray-200 flex flex-col items-center justify-center text-center text-gray-400 gap-6">
                <Sparkles size={80} strokeWidth={0.5} />
                <p className="text-lg font-bold uppercase tracking-widest opacity-40">Aguardando Captura para Auditoria</p>
             </div>
           )}
        </div>
      </div>
      
      {/* Listagem de Avarias Recentes */}
      <Card className="p-8">
         <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight mb-8">Últimos Danos Reportados em Campo</h3>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.filter((o: Order) => o.taxaAplicada).map((o: Order) => (
              <div key={o.id} className="p-4 bg-white border-2 border-red-50 rounded-3xl flex items-center gap-4 group hover:border-red-200 transition-all">
                 <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 font-black">
                    {o.id.split('-')[1]}
                 </div>
                 <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black text-red-600 uppercase">Avaria Confirmada</p>
                    <p className="text-sm font-bold text-gray-800 truncate">{o.nomeEvento}</p>
                    <p className="text-xs text-gray-400 truncate">{o.motivoTaxa}</p>
                 </div>
                 <ArrowRight size={20} className="text-gray-300 group-hover:translate-x-1 transition-all" />
              </div>
            ))}
         </div>
      </Card>
    </div>
  );
};

const ResultBox = ({ label, value, icon: Icon, color }: any) => (
  <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
     <p className="text-[9px] font-black text-gray-400 uppercase mb-1 tracking-widest">{label}</p>
     <div className="flex items-center gap-2">
        <Icon size={16} className={color} />
        <span className={`text-sm font-black uppercase ${color}`}>{value}</span>
     </div>
  </div>
);
